<script lang="ts">
import { fly } from 'svelte/transition'

import { testIds } from '@/constants'
import { type EmojiItem } from '@/modules/emojis'
import { Key } from '@/modules/textarea'
import { t } from '@/modules/translations'
import { clickOutside } from '@/utils/clickOutside'

type Props = {
	emojis: EmojiItem[]
	selectedIndex: number
	listboxId: string
	onEmojiSelect: (emoji: EmojiItem) => void
	onClose: () => void
}

let { emojis, selectedIndex, listboxId, onEmojiSelect, onClose }: Props = $props()

let listRef: HTMLDivElement | undefined = $state()

// Generate option ID for aria-activedescendant
const getOptionId = (index: number) => `${listboxId}-option-${index}`

// Scroll selected item into view
$effect(() => {
	if (!listRef || emojis.length === 0) return

	const selectedElement = listRef.children[selectedIndex] as HTMLElement | undefined
	if (selectedElement) {
		selectedElement.scrollIntoView({ block: 'nearest', behavior: 'instant' })
	}
})

const handleEmojiClick = (emoji: EmojiItem, event: MouseEvent) => {
	event.preventDefault()
	event.stopPropagation()
	onEmojiSelect(emoji)
}

const handleKeyDown = (event: KeyboardEvent) => {
	if (event.key === Key.Escape) {
		event.preventDefault()
		onClose()
	}
}
</script>

<!-- svelte-ignore a11y_interactive_supports_focus -->
<!-- The listbox is controlled via aria-activedescendant from the textarea, focus remains on the input -->
<div
	class="absolute bottom-full left-0 right-0 mb-3 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50"
	style="box-shadow: rgb(0 0 0 / 12%) 0px 1px 15px 1px;"
	role="listbox"
	id={listboxId}
	aria-label={$t('ariaLabel.inlineEmojiPicker.label')}
	data-testid={testIds.inlineEmojiPicker}
	onkeydown={handleKeyDown}
	use:clickOutside={onClose}
	in:fly={{ duration: 250, y: 10 }}
>
	<!-- Live region for screen reader announcements -->
	<div
		class="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0 [clip:rect(0,0,0,0)]"
		aria-live="polite"
		aria-atomic="true"
	>
		{#if emojis.length > 0}
			{$t('ariaLabel.inlineEmojiPicker.description', { count: String(emojis.length) })}
		{/if}
	</div>
	<div bind:this={listRef} class="max-h-64 overflow-y-auto overscroll-contain p-2">
		{#each emojis as emoji, index (emoji.id)}
			<!-- svelte-ignore a11y_interactive_supports_focus -->
			<!-- Options are selected via aria-activedescendant, not direct focus -->
			<div
				id={getOptionId(index)}
				role="option"
				aria-selected={index === selectedIndex}
				aria-label={emoji.name}
				class={[
					'w-full flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors',
					'hover:bg-slate-100',
					index === selectedIndex && 'bg-slate-100',
				]}
				onmousedown={(e) => handleEmojiClick(emoji, e)}
			>
				<span class="text-2xl flex-shrink-0" aria-hidden="true">{emoji.native}</span>
				<span class="text-sm text-slate-600 truncate">:{emoji.id}:</span>
			</div>
		{/each}
	</div>
</div>
